import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import DecoupageCatalogueRaisonneListForm from "@/components/cms/DecoupageCatalogueRaisonneListForm";
import { useSections, useUpdateCatalogueRaisonneSections } from "@/api/cms";
import { catalogueRaisonneSectionUpsertSchema } from "@/lib/cms/cms-types";

/**
 * Gestion COMPLÈTE des découpages du catalogue raisonné (onglets internes
 * Œuvres / Multiples), extraite de l'ancien champ Puck pour être réutilisée par
 * l'onglet dédié `/cms/advanced/decoupages` (page classique du CMS avancé).
 * Réutilise tel quel le formulaire `DecoupageCatalogueRaisonneForm` (piloté par
 * react-hook-form). Les données vivent au niveau CMS (`CatalogueRaisonneSection`,
 * partagées avec le site + les blocs Puck) → save via
 * `useUpdateCatalogueRaisonneSections`.
 *
 * ⚠️ Save reconstruit les lignes (`deleteMany` + `createMany`) mais **préserve
 * les IDs** déjà connus pour `cmsId + target` (sinon les refs Puck
 * `decoupageId` cassent). Le formulaire DOIT être initialisé (`useSections` +
 * garde `hasInit`) avant tout submit, sinon un enregistrement vide écraserait
 * le jeu de découpages. Éditer Œuvres ne touche pas Multiples.
 */

// Miroir du schéma de la page CMS catalogue-raisonné (petit).
const sectionSchema = z.object({
  type: z.string().optional(),
  target: z.enum(["ARTWORKS", "MULTIPRINTS"]).optional(),
  decoupages: z.array(catalogueRaisonneSectionUpsertSchema).optional(),
  sortByInsideDecoupage: z.string().optional(),
});
type SectionData = z.infer<typeof sectionSchema>;

type Target = "ARTWORKS" | "MULTIPRINTS";

function DecoupageTargetEditor({ target }: { target: Target }) {
  const cmsNew = useCmsNew();
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const cmsID = instanceData?.CMS?.id ?? "";
  const queryClient = useQueryClient();

  const { data: sections = [] } = useSections(cmsID, target);
  const { mutate: mutateSections, isPending } =
    useUpdateCatalogueRaisonneSections();

  const form = useForm<SectionData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      type: undefined,
      target,
      decoupages: [],
      sortByInsideDecoupage: "dateCreation:asc",
    },
  });

  const hasInit = useRef(false);
  useEffect(() => {
    if (sections && sections.length >= 0 && !hasInit.current) {
      form.reset({
        type: sections[0]?.type ?? "creationDate",
        target,
        decoupages: sections as SectionData["decoupages"],
        sortByInsideDecoupage:
          sections[0]?.sortByInsideDecoupage ?? "dateCreation:asc",
      });
      hasInit.current = true;
    }
  }, [sections, form, target]);

  const submit = (data: SectionData) => {
    if (!cmsID) {
      console.error("Découpages: cmsID manquant");
      return;
    }
    const activeType = data.type ?? "creationDate";
    const sortBy = data.sortByInsideDecoupage ?? "dateCreation:asc";
    // Parité ancien CMS : save = uniquement le type courant (remplace la cible).
    mutateSections(
      {
        cmsId: cmsID,
        type: activeType,
        target,
        decoupages: (data.decoupages ?? []).map((d, i) => ({
          ...d,
          type: activeType,
          sortByInsideDecoupage: sortBy,
          order: i + 1,
        })),
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("decoupageSuccessMessage"),
            variant: "success",
            duration: 3000,
          });
          // Rafraîchit toute prévisu lisant les sections (bloc Puck, etc.).
          queryClient.invalidateQueries({
            queryKey: ["sections", cmsID, target],
          });
        },
        onError: (error: unknown) => {
          console.error("Découpages: erreur de sauvegarde", error);
          toast({
            title: t("error"),
            description: t("decoupageErrorMessage"),
            variant: "error",
            duration: 3000,
          });
        },
      },
    );
  };

  return (
    <FormProvider {...form}>
      <div className="space-y-4">
        <DecoupageCatalogueRaisonneListForm
          form={form}
          target={target}
          sections={sections}
          cmsID={cmsID}
        />
        <div className="sticky bottom-4 flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-6 py-4 shadow-sm">
          <p className="text-sm text-slate-500">
            {form.formState.isDirty
              ? cmsPhrase(cmsNew, "dirtyUnsaved")
              : cmsPhrase(cmsNew, "dirtySaved")}
          </p>
          <Button
            type="button"
            onClick={form.handleSubmit(submit)}
            disabled={isPending}
          >
            {isPending ? cmsPhrase(cmsNew, "saving") : cmsPhrase(cmsNew, "saveDecoupages")}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}

function TabButton({
  active,
  onClick,
  children,
  ...props
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className, ...restProps } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      } ${className ?? ""}`}
      {...restProps}
    >
      {children}
    </button>
  );
}

/**
 * Corps du gestionnaire de découpages : onglets internes Œuvres / Multiples.
 * En mode CR mixte (`isMixedCRMode`), le catalogue œuvres+multiples est fusionné
 * → un seul jeu de découpages (cible ARTWORKS), l'onglet Multiples est masqué.
 */
export function DecoupageManagerBody({
  isMixedCRMode = false,
}: {
  isMixedCRMode?: boolean;
}) {
  const cmsNew = useCmsNew();
  const [tab, setTab] = useState<Target>("ARTWORKS");
  const effectiveTab: Target = isMixedCRMode ? "ARTWORKS" : tab;
  const tabId = (value: Target) => `decoupage-tab-${value.toLowerCase()}`;
  const panelId = "decoupage-tabpanel";

  const onTabsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isMixedCRMode) return;
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const nextTab =
      tab === "ARTWORKS"
        ? ("MULTIPRINTS" as const)
        : ("ARTWORKS" as const);
    setTab(nextTab);
  };

  return (
    <div className="space-y-4">
      {isMixedCRMode ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {cmsPhrase(cmsNew, "mixedCatalogHint")}
        </div>
      ) : (
        <div
          className="inline-flex gap-1 rounded-lg bg-slate-100 p-1"
          role="tablist"
          aria-label={cmsPhrase(cmsNew, "targetTypeAria")}
          onKeyDown={onTabsKeyDown}
        >
          <TabButton
            active={effectiveTab === "ARTWORKS"}
            onClick={() => setTab("ARTWORKS")}
            role="tab"
            id={tabId("ARTWORKS")}
            aria-selected={effectiveTab === "ARTWORKS"}
            aria-controls={panelId}
            tabIndex={effectiveTab === "ARTWORKS" ? 0 : -1}
          >
            {cmsPhrase(cmsNew, "artworksTab")}
          </TabButton>
          <TabButton
            active={effectiveTab === "MULTIPRINTS"}
            onClick={() => setTab("MULTIPRINTS")}
            role="tab"
            id={tabId("MULTIPRINTS")}
            aria-selected={effectiveTab === "MULTIPRINTS"}
            aria-controls={panelId}
            tabIndex={effectiveTab === "MULTIPRINTS" ? 0 : -1}
          >
            {cmsPhrase(cmsNew, "multiplesTab")}
          </TabButton>
        </div>
      )}
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(effectiveTab)}
        tabIndex={0}
      >
        {/* key force le remount (ré-init du form) au changement de cible */}
        <DecoupageTargetEditor key={effectiveTab} target={effectiveTab} />
      </div>
    </div>
  );
}
